'use client'

import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface DevGateProps {
	children: React.ReactNode
}

export function DevGate({ children }: DevGateProps) {
	const t = useTranslations('devGate')
	const [status, setStatus] = useState<'loading' | 'required' | 'valid'>('loading')
	const [password, setPassword] = useState('')
	const [error, setError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)

	useEffect(() => {
		checkDevGate()
	}, [])

	async function checkDevGate() {
		try {
			const response = await fetch('/api/dev-gate')
			const data = await response.json()
			if (!data.required || data.valid) {
				setStatus('valid')
			} else {
				setStatus('required')
			}
		} catch {
			setStatus('valid')
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setError('')
		setIsSubmitting(true)
		try {
			const response = await fetch('/api/dev-gate', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ password }),
			})
			if (response.ok) {
				setStatus('valid')
			} else {
				setError(t('invalidPassword'))
			}
		} catch {
			setError(t('error'))
		} finally {
			setIsSubmitting(false)
		}
	}

	if (status === 'loading') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="animate-pulse text-muted-foreground">{t('loading')}</div>
			</div>
		)
	}

	if (status === 'required') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-sm">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
							<Lock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
						</div>
						<CardTitle>{t('title')}</CardTitle>
						<CardDescription>{t('description')}</CardDescription>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Input
									type="password"
									placeholder={t('placeholder')}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									autoFocus
								/>
								{error && <p className="text-sm text-red-500">{error}</p>}
							</div>
							<Button
								type="submit"
								className="w-full bg-blue-600 hover:bg-blue-700 text-white"
								disabled={isSubmitting || !password}
							>
								{isSubmitting ? t('verifying') : t('submit')}
							</Button>
						</form>
					</CardContent>
				</Card>
			</div>
		)
	}

	return <>{children}</>
}
